import React, { FC, useEffect, useState } from 'react';
import Modal from 'react-modal';
import amplitude from 'amplitude-js';
import { useNavigate } from 'react-router-dom';

import { DiscountModalData } from 'types';

import Header from 'components/Header';
import { ReactComponent as Apple } from 'assets/images/apple.svg';
import { ReactComponent as Google } from 'assets/images/google.svg';
import { ReactComponent as Cross } from 'assets/images/cross.svg';

import sendAnalyticsEvent from 'utils/sendAnalyticsEvent';
import { useAuth } from '../contexts/AuthContext';
import { startPolarCheckout } from '../lib/polar';

interface Props {
    isModalOpen: boolean;
    logoUrl: string;
    logoLink: string;
    price: string;
    period: string;
    link: string;
    planId: string;
    isDiscount: boolean;
    discountModalData?: DiscountModalData;
    setIsModalOpen: (arg: boolean) => void;
    setIsDiscount: (arg: boolean) => void;
}
const PaymentModal: FC<Props> = ({
    setIsModalOpen,
    isModalOpen,
    logoUrl,
    logoLink,
    price,
    period,
    link,
    planId,
    discountModalData,
    setIsDiscount,
    isDiscount,
}) => {
    const [isShowDiscount, setIsShowDiscount] = useState(false);
    const [isSendAnalytic, setIsSendAnalytic] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();
    const localQuizId = sessionStorage.getItem('quizId');

    const backgroundImage = isShowDiscount ? discountModalData?.image : '';

    const handleOnClick = () => {
        setIsModalOpen(false);
        setIsDiscount(true);
        setIsShowDiscount(false);
    };

    const handlePaymentClick = async (paymentMethod: string) => {
        try {
            setIsLoading(true);

            // Extract numeric price value
            const priceValue = parseFloat(price.replace(/[^0-9.]/g, ''));

            // Track payment initiation
            sendAnalyticsEvent({
                eventName: 'payment_method_selected',
                data: {
                    payment_method: paymentMethod,
                    plan_id: planId,
                    plan_period: period,
                    price: priceValue,
                    is_discount: isDiscount,
                    screen_name: isDiscount ? 'cancel_offer' : 'offer',
                },
            });

            await startPolarCheckout({
                email: user?.email || undefined,
                returnPath: `/subscription-success/${localQuizId}`,
                metadata: {
                    userId: user?.id || '',
                    planId,
                    quizId: localQuizId || '',
                },
            });
        } catch (error) {
            console.error('Payment error:', error);
            setIsLoading(false);
            alert('Failed to initialize payment. Please try again.');
        }
    };

    useEffect(() => {
        if (!isSendAnalytic && isModalOpen) {
            sendAnalyticsEvent({
                eventName: 'checkout_initiated',
                data: { screen_name: isDiscount ? 'cancel_offer' : 'offer' },
            });
            setIsSendAnalytic(true);
        }
        if (isSendAnalytic && !isModalOpen) {
            setIsSendAnalytic(false);
        }
    }, [isSendAnalytic, isModalOpen]);

    const renderScreens = () => {
        if (!isShowDiscount) {
            return (
                <div className="modal__wrapper">
                    <button
                        type="button"
                        className="modal__back-button"
                        onClick={() => {
                            setIsShowDiscount(true);
                        }}
                    >
                        Close <Cross className="modal__cross" />
                    </button>
                    <h2 className="modal__title">Checkout</h2>
                    <p className="modal__headline">
                        Selected plan <span>(50% off)</span>
                    </p>
                    <p className="modal__total">
                        Total amount to pay <span dangerouslySetInnerHTML={{ __html: price }} /> per {period}
                    </p>
                    <button 
                        className="modal__button" 
                        type="button"
                        onClick={() => handlePaymentClick('polar')}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Loading...' : 'Continue to Payment'}
                    </button>
                    <p style={{ fontSize: '12px', textAlign: 'center', marginTop: '10px', color: '#666' }}>
                        Secure payment powered by Polar
                    </p>
                </div>
            );
        }
        return (
            <div className="modal__discount">
                <p className="modal__discount-present">
                    <span>{discountModalData?.discount}</span>
                </p>
                <p className="modal__discount-title">{discountModalData?.title}</p>
                <p className="modal__discount-text">{discountModalData?.text}</p>
                <button className="modal__discount-button" type="button" onClick={handleOnClick}>
                    {discountModalData?.buttonText}
                </button>
            </div>
        );
    };

    return (
        <Modal isOpen={isModalOpen} overlayClassName="modal__overlay" className="modal" bodyOpenClassName="modal__body">
            <div className="modal__inner-wrap" style={{ backgroundImage: `url(${backgroundImage})` }}>
                <Header logoUrl={logoUrl} logoLink={logoLink} isHiddenProgress />
                {renderScreens()}
            </div>
        </Modal>
    );
};

export default PaymentModal;
